import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  canViewDuplicateInRanking, duplicateNoticeKey,
} from '@/lib/template-suggestions/duplicateNoticeKey';
import { normalizeSuggestionUrl } from '@/lib/template-suggestions/normalizeSuggestionUrl';
import type { TemplateSuggestion } from '@/lib/template-suggestions/types';

export interface SuggestTemplateInput {
  url: string;
  title: string;
  useCase: string;
  note?: string;
}

interface SuggestTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submitting: boolean;
  duplicate: TemplateSuggestion | null;
  errorMessage: string | null;
  onSubmit: (input: SuggestTemplateInput) => void;
  onViewDuplicate: (suggestion: TemplateSuggestion) => void;
}

export function SuggestTemplateDialog({
  open, onOpenChange, submitting, duplicate, errorMessage, onSubmit, onViewDuplicate,
}: SuggestTemplateDialogProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [useCase, setUseCase] = useState('');
  const [note, setNote] = useState('');

  // Radix keeps this component mounted across close/reopen — only `open`
  // toggles. Clear the draft on a fresh open (not on close) so a dialog left
  // open after a duplicate result keeps what the user typed, but reopening
  // for a second suggestion never starts pre-filled with the first.
  useEffect(() => {
    if (!open) return;
    setUrl('');
    setTitle('');
    setUseCase('');
    setNote('');
  }, [open]);

  const parsed = useMemo(() => normalizeSuggestionUrl(url), [url]);
  const canSubmit = !!parsed && !!title.trim() && !!useCase.trim() && !submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('templateIdeas.suggest')}</DialogTitle>
          <DialogDescription>{t('templateIdeas.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="suggestion-url">{t('templateIdeas.form.url')}</Label>
            <Input
              id="suggestion-url"
              data-testid="suggestion-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('templateIdeas.form.urlPlaceholder')}
            />
            {parsed && (
              <Badge variant="outline" className="capitalize" data-testid="suggestion-platform">
                {parsed.platform}
              </Badge>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="suggestion-title">{t('templateIdeas.form.title')}</Label>
            <Input
              id="suggestion-title"
              data-testid="suggestion-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('templateIdeas.form.titlePlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="suggestion-use-case">{t('templateIdeas.form.useCase')}</Label>
            <Input
              id="suggestion-use-case"
              data-testid="suggestion-use-case"
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              placeholder={t('templateIdeas.form.useCasePlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="suggestion-note">{t('templateIdeas.form.note')}</Label>
            <Textarea
              id="suggestion-note"
              data-testid="suggestion-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('templateIdeas.form.notePlaceholder')}
            />
          </div>

          {duplicate && (
            <div
              className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
              data-testid="suggestion-duplicate"
            >
              {/* Copy follows the duplicate's own status: only a public row is
                  votable, so only that one may promise a vote. */}
              <p>{t(duplicateNoticeKey(duplicate.status))}</p>

              {/* Show what was duplicated inline. The row is often not on the
                  page at all (an own pending row, an admin-only row, or one
                  ranked past the page cap), so the user cannot be sent to look
                  for it. */}
              <div
                className="rounded border border-border bg-background p-2"
                data-testid="suggestion-duplicate-summary"
              >
                <p className="truncate font-medium">{duplicate.title}</p>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Badge variant="outline" className="capitalize">{duplicate.platform}</Badge>
                  <Badge variant="secondary">{t(`templateIdeas.status.${duplicate.status}`)}</Badge>
                </div>
              </div>

              {canViewDuplicateInRanking(duplicate.status) && (
                <Button
                  variant="link"
                  className="h-auto p-0"
                  data-testid="suggestion-duplicate-view"
                  onClick={() => onViewDuplicate(duplicate)}
                >
                  {t('templateIdeas.duplicate.action')}
                </Button>
              )}
            </div>
          )}

          {errorMessage && (
            <p className="text-sm text-destructive" data-testid="suggestion-error">{errorMessage}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('templateIdeas.form.cancel')}
          </Button>
          <Button
            data-testid="suggestion-submit"
            disabled={!canSubmit}
            onClick={() => onSubmit({ url: url.trim(), title: title.trim(), useCase: useCase.trim(), note: note.trim() || undefined })}
          >
            {t('templateIdeas.form.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
